export interface QuizQuestion {
  id: number;
  block: 'sobrecargaMental' | 'comunicacao' | 'delegacao' | 'financeiro';
  blockLabel: string;
  question: string;
  inverted: boolean;
}

export interface QuizProfile {
  id: 'operacional' | 'manualidade' | 'transicao' | 'estrategica';
  title: string;
  description: string;
  supportTitle: string;
  solutions: string[];
  minScore: number;
  maxScore: number;
}

export const likertOptions = [
  { value: 5, label: 'Concordo Totalmente' },
  { value: 4, label: 'Concordo' },
  { value: 3, label: 'Neutro' },
  { value: 2, label: 'Discordo' },
  { value: 1, label: 'Discordo Totalmente' },
];

export const quizQuestions: QuizQuestion[] = [
  {
    id: 1,
    block: 'sobrecargaMental',
    blockLabel: 'Sobrecarga Mental',
    question: 'Somente eu sei os detalhes técnicos do evento que vou organizar?',
    inverted: false,
  },
  {
    id: 2,
    block: 'sobrecargaMental',
    blockLabel: 'Sobrecarga Mental',
    question: 'Com frequência fico preocupado(a) achando que esqueci algo importante para um evento.',
    inverted: false,
  },
  {
    id: 3,
    block: 'sobrecargaMental',
    blockLabel: 'Sobrecarga Mental',
    question: 'Eu posso tirar férias ou folgas sem que a operação da empresa pare completamente?',
    inverted: true,
  },
  {
    id: 4,
    block: 'comunicacao',
    blockLabel: 'Comunicação',
    question: 'Eu utilizo o WhatsApp para controlar os detalhes dos meus eventos como datas, alterações e localização.',
    inverted: false,
  },
  {
    id: 5,
    block: 'comunicacao',
    blockLabel: 'Comunicação',
    question: 'Após o evento, mantenho um relacionamento estruturado com meus clientes (follow-up, recompra, feedback)',
    inverted: true,
  },
  {
    id: 6,
    block: 'comunicacao',
    blockLabel: 'Comunicação',
    question: 'Eu crio os propostas manualmente - não utilizo nenhuma ferramenta digital (ex.: menu digital)',
    inverted: false,
  },
  {
    id: 7,
    block: 'delegacao',
    blockLabel: 'Delegação',
    question: 'O time que executa o evento possui fichas técnicas e cronogramas claros para trabalhar',
    inverted: true,
  },
  {
    id: 8,
    block: 'delegacao',
    blockLabel: 'Delegação',
    question: 'Tenho processos claros e documentados para vendas, execução e pós-evento.',
    inverted: true,
  },
  {
    id: 9,
    block: 'financeiro',
    blockLabel: 'Financeiro',
    question: 'Sei exatamente qual é minha margem de lucro real após cada evento realizado.',
    inverted: true,
  },
  {
    id: 10,
    block: 'financeiro',
    blockLabel: 'Financeiro',
    question: 'Meus contratos e cobrança de pagamentos são feitos manualmente?',
    inverted: false,
  },
];

export const quizProfiles: QuizProfile[] = [
  {
    id: 'operacional',
    title: 'Operacional Exaustivo',
    description: 'Seu negócio depende excessivamente de você. O risco de esgotamento é alto, colocando o crescimento do seu negócio em risco.',
    supportTitle: 'Tirar você do caos operacional e devolver controle, clareza e fôlego',
    solutions: [
      'Central de Pedidos: Seus eventos controlados 100% em uma única ferramenta',
      'Digitalização do seu negócio: Automatize seu fluxo de venda e reduza a manualidade excessiva',
      'Painel Financeiro: Trazendo visibilidade analítica do seu negócio para tomada de decisões',
    ],
    minScore: 0,
    maxScore: 20,
  },
  {
    id: 'manualidade',
    title: 'Manualidade Excessiva',
    description: 'Sua empresa consegue realizar eventos ao longo do ano mas ainda possui muito processo manual, deixando a jornada muito onerosa para você e/ou para o seu time de vendas.',
    supportTitle: 'Aumentar produtividade e capacidade de atendimento sem aumentar o esforço',
    solutions: [
      'Digitalização do seu negócio: Automatize seu fluxo de venda e reduza a manualidade excessiva',
      'Calendário de Eventos: Integração da sua agenda 360º',
      'Indicadores e Métricas: Organização do funil de vendas e histórico de clientes',
    ],
    minScore: 21,
    maxScore: 30,
  },
  {
    id: 'transicao',
    title: 'Gestor em Transição',
    description: 'Você lidera bem sua empresa mas há uma trava nos processos que limita a delegação de tarefas e consequentemente o seu crescimento.',
    supportTitle: 'Transformar intenção de crescimento em escala real e sustentável',
    solutions: [
      'Digitalização do seu negócio: Automatize seu fluxo de venda e reduza a manualidade excessiva',
      'Ficha Técnica e Precificação: Apoiando na construção de processos e precificação inteligente',
      'Indicadores e Métricas: Organização do funil de vendas e histórico de clientes',
    ],
    minScore: 31,
    maxScore: 40,
  },
  {
    id: 'estrategica',
    title: 'Gestão Estratégica',
    description: 'Parabéns! Você está no caminho certo. Hoje, sua empresa possui processos, consciência financeira e organização básica para delegar, crescer e alcançar muito lucro.',
    supportTitle: 'Maximizar resultados, margem e controle estratégico do negócio',
    solutions: [
      'Digitalização do seu negócio: Automatize seu fluxo de venda e aumente sua representatividade na categoria',
      'Indicadores e Métricas: Automação avançada da operação e do financeiro',
      'Recomendação Financeira: CAC ideal, % de Margem, Definição de Metas e Alcance mensal',
    ],
    minScore: 41,
    maxScore: 50,
  },
];

export interface BlockScores {
  sobrecargaMental: number;
  comunicacao: number;
  delegacao: number;
  financeiro: number;
}

export const calculateScore = (answers: Record<number, number>): number => {
  let totalScore = 0;
  
  quizQuestions.forEach((q, index) => {
    const answer = answers[index] || 3; // Default to neutral if not answered
    
    if (q.inverted) {
      // For inverted questions: high agreement (5) = good management = high score
      totalScore += answer;
    } else {
      // For normal questions (1, 2): high agreement = pain = low score
      // Invert the value: 5 becomes 1, 1 becomes 5
      totalScore += (6 - answer);
    }
  });
  
  return totalScore;
};

export const calculateBlockScores = (answers: Record<number, number>): BlockScores => {
  const blockScores: BlockScores = {
    sobrecargaMental: 0,
    comunicacao: 0,
    delegacao: 0,
    financeiro: 0,
  };
  
  quizQuestions.forEach((q, index) => {
    const answer = answers[index] || 3;
    let score: number;
    
    if (q.inverted) {
      score = answer;
    } else {
      score = 6 - answer;
    }
    
    blockScores[q.block] += score;
  });
  
  return blockScores;
};

export const getProfile = (totalScore: number): QuizProfile => {
  return quizProfiles.find(
    (p) => totalScore >= p.minScore && totalScore <= p.maxScore
  ) || quizProfiles[0];
};
